import { Game } from './engine/game.js';
import { TitleScreen } from './menus/title-screen.js';

const status = document.getElementById('loading-status');
const loading = document.getElementById('loading');
const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;

const setStatus = (msg: string) => {
  if (status) status.textContent = msg;
};

async function main() {
  if (!canvas) throw new Error('No #game-canvas in DOM');

  setStatus('Loading physics engine…');
  const { default: RAPIER } = await import('@dimforge/rapier3d-compat');
  await RAPIER.init();

  setStatus('Ready.');
  loading?.classList.add('fade-out');
  setTimeout(() => loading?.remove(), 500);

  // Allow URL params to bypass the title screen for quick dev iteration:
  //   ?autostart=1&ai=2&skill=rookie&track=speedway
  //   ?demo=1 → spawns 3 AI Champions on the chosen track, no keyboard input
  //     (used for action screenshots and unattended demo loops)
  const params = new URLSearchParams(window.location.search);
  const autostart = params.get('autostart');
  const demo = params.get('demo');
  let sel: {
    aiCount: number;
    aiSkill: 'trainee' | 'rookie' | 'ace' | 'champion';
    trackId: 'sandbox' | 'speedway' | 'funhouse';
    demoMode: boolean;
  };
  if (demo) {
    sel = {
      aiCount: 3,
      aiSkill: (params.get('skill') as typeof sel.aiSkill) ?? 'champion',
      trackId: (params.get('track') as typeof sel.trackId) ?? 'sandbox',
      demoMode: true,
    };
  } else if (autostart) {
    sel = {
      aiCount: clampInt(Number(params.get('ai') ?? '0'), 0, 3),
      aiSkill: (params.get('skill') as typeof sel.aiSkill) ?? 'rookie',
      trackId: (params.get('track') as typeof sel.trackId) ?? 'sandbox',
      demoMode: false,
    };
  } else {
    const title = new TitleScreen();
    const titleSel = await title.show();
    sel = { ...titleSel, demoMode: false };
  }

  // Start the game with the picked config.
  const game = new Game({
    canvas,
    rapier: RAPIER,
    aiCount: sel.aiCount,
    aiSkill: sel.aiSkill,
    trackId: sel.trackId,
    demoMode: sel.demoMode,
  });
  await game.start();

  // Expose for debugging in dev.
  if (import.meta.env.DEV) {
    (window as unknown as { polyloop: Game }).polyloop = game;
  }
}

function clampInt(v: number, lo: number, hi: number): number {
  if (Number.isNaN(v)) return lo;
  const i = Math.round(v);
  return i < lo ? lo : i > hi ? hi : i;
}

main().catch((err: unknown) => {
  console.error('Polyloop failed to start:', err);
  setStatus(`Failed: ${(err as Error).message ?? err}`);
});
